import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Edit, MessageSquare, Calendar, Trash2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProprietarioForm from "@/components/proprietarios/ProprietarioForm";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/lib/supabase";

// Mock data with registration and last update date
const mockOwners = [
  {
    id: "1",
    name: "João Silva",
    email: "joao@email.com",
    phone: "(11) 99999-9999",
    whatsapp: "5511999999999",
    properties: [],
    registrationDate: new Date("2024-01-15"),
    lastUpdateDate: new Date("2024-03-20"),
  },
];

export default function ProprietariosIndex() {
  const [owners, setOwners] = useState(mockOwners);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleEdit = (ownerId: string) => {
    const owner = owners.find(o => o.id === ownerId);
    if (owner) {
      navigate(`/proprietarios/${ownerId}/editar`, { 
        state: { owner } 
      });
    } else {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Proprietário não encontrado.",
      });
    }
  };

  const handleDelete = (ownerId: string) => {
    const updatedOwners = owners.filter(owner => owner.id !== ownerId);
    setOwners(updatedOwners);
    toast({
      title: "Proprietário excluído",
      description: "O proprietário foi excluído com sucesso!",
    });
  };

  const handleWhatsAppRedirect = (whatsapp: string) => {
    const whatsappUrl = `https://wa.me/${whatsapp}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleExportCSV = () => {
    const headers = ["Nome", "Email", "Telefone", "WhatsApp", "Data de Cadastro", "Última Atualização"];
    const csvContent = [
      headers.join(","),
      ...owners.map(owner => [
        owner.name,
        owner.email,
        owner.phone,
        owner.whatsapp,
        format(owner.registrationDate, "dd/MM/yyyy"),
        format(owner.lastUpdateDate, "dd/MM/yyyy")
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "proprietarios.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Exportação concluída",
      description: "Os dados foram exportados com sucesso!",
    });
  };

  const handleSubmit = async (data: any) => {
    try {
      // Here you would typically make an API call to save the data
      console.log("Form data:", data);
      toast({
        title: "Sucesso!",
        description: "Proprietário cadastrado com sucesso.",
      });
      setShowForm(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro!",
        description: "Ocorreu um erro ao cadastrar o proprietário.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-gray-900">
            Proprietários
          </h1>
          <p className="mt-2 text-gray-600">
            Gerencie os proprietários e seus imóveis
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCSV}>
            <FileDown className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="w-4 h-4 mr-2" />
            {showForm ? "Voltar" : "Novo Proprietário"}
          </Button>
        </div>
      </div>

      {showForm ? (
        <div className="bg-white p-6 rounded-xl shadow-card">
          <ProprietarioForm onSubmit={handleSubmit} />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Data de Cadastro</TableHead>
                <TableHead>Última Atualização</TableHead>
                <TableHead>Imóveis</TableHead>
                <TableHead className="w-[150px]">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {owners.map((owner) => (
                <TableRow key={owner.id}>
                  <TableCell className="font-medium">
                    {owner.name}
                  </TableCell>
                  <TableCell>{owner.email}</TableCell>
                  <TableCell>{owner.phone}</TableCell>
                  <TableCell>
                    {format(owner.registrationDate, "dd 'de' MMMM 'de' yyyy", {
                      locale: ptBR,
                    })}
                  </TableCell>
                  <TableCell>
                    {format(owner.lastUpdateDate, "dd 'de' MMMM 'de' yyyy", {
                      locale: ptBR,
                    })}
                  </TableCell>
                  <TableCell>{owner.properties.length}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleWhatsAppRedirect(owner.whatsapp)}
                      >
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(owner.id)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(owner.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}